/* eslint-disable max-len */
/* eslint-disable no-param-reassign */
import React, { useContext, useCallback } from 'react';
import { ViewModelServiceContext, ViewModelForm, useDataRefresh } from '@xengage/gw-portals-viewmodel-react';
import { useTranslator } from '@jutro/locale';
import { MLDriverVehicleUtil } from 'ml-portals-util-react';
import { Driver, Person } from 'gw-capability-quoteandbind';
import { EntityUtil } from '@xengage/gw-portals-util-js';
import { useModal } from '@jutro/components';
import { FormModalPopover, MLTooltipComponent, platformMessages as commonMessages } from 'ml-components-platform-react';
import { MLNameDetailsComponent } from 'ml-components-common-react';
// eslint-disable-next-line import/no-unresolved
import appConfig from 'app-config';

import _ from 'lodash';
import PropTypes from 'prop-types';
import metadata from './SpousePopoverComponent.metadata.json5';
import styles from './SpousePopoverComponent.module.scss';
import messages from './SpousePopoverComponent.messages';

import addSpouseMessages from './AddSpousePopup.messages';
import addSpouseMetadata from './AddSpousePopup.metadata.json5';
import addSpouseModalBodyStyles from './AddSpousePopup.module.scss';

function SpousePopoverComponent(props) {
    const modalApi = useModal();
    const translator = useTranslator();

    const {
        submissionVM,
        driverVM,
        driverList,
        handleSpouseModelChange,
        updateModifiers,
        isPolicyChange,
        isCompRaterSpouseFeature,
        showValidationErrors,
        isPersonalUmbrella = false,
        disableAddNewSpouse = false
    } = props;

    const ADDPERSONID = 'ADDPERSONID';

    const { refreshData } = useDataRefresh();
    const viewModelService = useContext(ViewModelServiceContext);
    const isModernProduct = _.get(submissionVM, "isModernProduct_Ext.value", false);
    const relationshipTypelist = viewModelService.productMetadata.get('pc').types.getTypelist('InsuredRelationType_ML').codes;
    const SELF = (() => {
        if (relationshipTypelist.filter((rel) => rel.code === 'self')[0]) {
            return relationshipTypelist.filter((rel) => rel.code === 'self')[0].code;
        }
        return 'self';
    })();
    const SPOUSE = (() => {
        if (relationshipTypelist.filter((rel) => rel.code === 'spouse')[0]) {
            return relationshipTypelist.filter((rel) => rel.code === 'spouse')[0].code;
        }
        return 'spouse';
    })();
    const isGPA = appConfig.portalCode === 'GPA';
    const isAMP = appConfig.portalCode === 'AMP';
    const isQB = appConfig.portalCode === 'QB';
    const isConsumer = appConfig.agentendorsement === 'false';

    const {
        checkNoSpouse,
        getFullName,
        populateDriverPerson,
        validateDriverAddition,
        getFilteredGender
    } = MLDriverVehicleUtil();

    const getCurrentDriverContactValue = () => {
        return isPersonalUmbrella
            ? _.get(driverVM, 'contact.value', {})
            : _.get(driverVM, 'person.value', {});
    };

    const getSpouseLabel = useCallback(() => {
        const currentPerson = isPersonalUmbrella
            ? _.get(driverVM, 'contact.value', {})
            : _.get(driverVM, 'person.value', {});

        let fullName = getFullName(currentPerson);

        fullName = isPolicyChange ? fullName : fullName?.toUpperCase();

        return translator(messages.spouseLabel, { spouse: fullName });
    }, [driverVM, getFullName, isPolicyChange, isPersonalUmbrella, translator]);

    const getCurrentDriverFixedId = () => {
        return isPersonalUmbrella
            ? _.get(driverVM, 'value.fixedID') || _.get(driverVM, 'value.tempID')
            : _.get(driverVM, 'value.fixedId') || _.get(driverVM, 'value.tempID');
    };

    const getDriverFixedId = (driver) => {
        return isPersonalUmbrella
            ? driver?.fixedID || driver?.tempID
            : driver?.fixedId || driver?.tempID;
    };

    const getDriverPerson = (driver) => {
        return isPersonalUmbrella ? driver?.contact : driver?.person;
    };

    const getCurrentDriverPersonPublicID = () => {
        return isPersonalUmbrella
            ? (
                _.get(driverVM, 'contact.publicID.value') ||
                _.get(driverVM, 'contact.tempID.value') ||
                _.get(driverVM, 'value.contact.publicID')
            )
            : (
                _.get(driverVM, 'person.publicID.value') ||
                _.get(driverVM, 'person.tempID.value') ||
                _.get(driverVM, 'value.person.publicID')
            );
    };

    const getDriverPersonPublicID = (driver) => {
        const person = getDriverPerson(driver);

        return person?.publicID || person?.tempID;
    };

    const getSpouseIDValue = () => {
        if (isPersonalUmbrella) {
            return _.get(driverVM, 'value.spouseID_ML');
        }

        return _.get(driverVM, 'spouseID_ML.value');
    };

    const setSpouseIDValue = (value) => {
        if (isPersonalUmbrella) {
            _.set(driverVM, 'value.spouseID_ML', value);
            return;
        }

        _.set(driverVM, 'spouseID_ML.value', value);
    };

    const getSpouseDisplayList = (person = {}) => {
        const displayName =
            person.displayName ||
            person.displayFullName_Ext ||
            [person.firstName, person.lastName].filter(Boolean).join(' ');

        return displayName === 'Unnamed'
            ? [person.firstName, person.lastName].filter(Boolean).join(' ')
            : displayName;
    };

    const buildSpouseList = () => {
        if (!isPersonalUmbrella) {
            const householdMemberSpouseList = submissionVM.lobData.personalAuto.coverables.drivers.value.filter(
                (a) => (
                    (a.status_ML === 'abroad' || a.status_ML === 'never_licensed') &&
                    ((isModernProduct && a.type_ML === 'nondriver') || a.type_ML === 'household')
                )
            );

            const newSNI = submissionVM?.baseData?.secondaryNamedInsured_ML?.value;
            const existingPeople = [...driverList, ...householdMemberSpouseList];
            const isDuplicate = existingPeople.some(item => item?.person?.addressBookUID_ML === newSNI?.addressBookUID_ML);
            const spouseList = isModernProduct && newSNI && !isDuplicate ?  [{ person: newSNI }, ...existingPeople] : [...existingPeople]

            return _.reduce(spouseList, (results, d) => {
                if (
                    (driverVM.value.fixedId !== d.fixedId) ||
                    (driverVM.value.tempID !== d.tempID)
                ) {
                    d.person.displayName = getFullName(d.person);
                    results.push(d.person);
                }

                return results;
            }, []);
        }

        const currentDriverFixedId = getCurrentDriverFixedId();

        return _.reduce(driverList || [], (results, driver) => {
            const driverFixedId = getDriverFixedId(driver);

            if (String(currentDriverFixedId) !== String(driverFixedId)) {
                const person = getDriverPerson(driver);

                if (!_.isEmpty(person)) {
                    results.push({
                        publicID: person.publicID || person.tempID,
                        tempID: person.tempID,
                        displayName:
                            person.displayName ||
                            person.displayFullName_Ext ||
                            getFullName(person),
                        firstName: person.firstName,
                        lastName: person.lastName
                    });
                }
            }
            
            return results;
        }, []);
    };

    const personList = disableAddNewSpouse
        ? buildSpouseList()
        : [
            ...buildSpouseList(),
            {
                publicID: ADDPERSONID,
                displayName: translator(messages.addNewPersonAsSpouse)
            }
        ];

    const addNewSpouseDriverModel = useCallback(() => {
        const driverObj = new Driver();
        driverObj.tempID = EntityUtil.nextId().toString();
        driverObj.person = new Person();
        driverObj.person.tempID = EntityUtil.nextId().toString();
        driverObj.person.primaryAddress = submissionVM.baseData.policyAddress.value;
        driverObj.person.middleName = null;
        driverObj.licenseState = driverObj.person.primaryAddress.state;

        // set partialDriverAdditionFlag_ML to "true" to avoid requiredness for other fields
        driverObj.partialDriverAdditionFlag_ML = true;
        driverObj.type_ML = 'rated';
        driverObj.relationshipToInsured_ML = 'spouse';
        driverObj.error = {
            message: null
        };
        driverObj.editMode_ML = true;

        const { _xCenter, _dtoName } = submissionVM.lobData.personalAuto.coverables.drivers;
        return viewModelService.create(driverObj, _xCenter, _dtoName);
    }, [submissionVM, viewModelService]);

    const setOtherDriverInfo = (otherDriver) => {
        otherDriver.spouseID_ML = driverVM?.person?.publicID?.value;
        const isDriverSelf = driverVM?.relationshipToInsured_ML && driverVM?.relationshipToInsured_ML?.value && driverVM?.relationshipToInsured_ML.value.code === SELF;
        otherDriver.relationshipToInsured_ML = isDriverSelf ? SPOUSE : otherDriver.relationshipToInsured_ML;
        otherDriver.maritalStatus_ML = 'M';
    };

    const setCorrespondingSpouse = (value) => {
        if (value === ADDPERSONID) {
            onAddSpouseClick();
            return;
        }

        if (isPersonalUmbrella) {
            setSpouseIDValue(value);

            const selectedSpouseDriver = (driverList || []).find((driver) => {
                return String(getDriverPersonPublicID(driver)) === String(value);
            });

            if (selectedSpouseDriver) {
                const currentDriverPersonPublicID = getCurrentDriverPersonPublicID();

                _.set(driverVM, 'value.spouseID_ML', value);
                _.set(driverVM, 'value.maritalStatus_Ext', 'M');

                if (_.has(driverVM, 'contact.maritalStatus.value')) {
                    _.set(driverVM, 'contact.maritalStatus.value', 'M');
                }

                selectedSpouseDriver.spouseID_ML = currentDriverPersonPublicID;
                selectedSpouseDriver.maritalStatus_Ext = 'M';

                if (selectedSpouseDriver.contact) {
                    selectedSpouseDriver.contact.maritalStatus = 'M';
                }

                const currentRelationship =
                    _.get(driverVM, 'relationshipToInsured.value.code') ||
                    _.get(driverVM, 'relationshipToInsured.value') ||
                    _.get(driverVM, 'value.relationshipToInsured');

                if (currentRelationship === SELF) {
                    selectedSpouseDriver.relationshipToInsured = SPOUSE;
                }
            }

            handleSpouseModelChange?.(driverVM);
            refreshData();
            return;
        }

        const isSelectedSpouseAlreadyPaired = (selectedSpouseId) => {
          const currentPersonId = getCurrentDriverPersonPublicID();

          const selectedDriver = driverList.find(
            (driver) =>
              String(getDriverPersonPublicID(driver)) ===
              String(selectedSpouseId),
          );

          return Boolean(
            selectedDriver?.spouseID_ML &&
              String(selectedDriver.spouseID_ML) !== String(currentPersonId),
          );
        };

        // existing Auto behavior

        const alreadyPaired = isQB && isSelectedSpouseAlreadyPaired(value);

        if (driverVM.spouseID_ML) {
            driverVM.spouseID_ML.value = value;
        }

        if (alreadyPaired) {
            refreshData();
            return;
        };

        const otherDriver = driverList.find((driver) => {
            return isQB
                ? String(getDriverPersonPublicID(driver)) === String(value)
                : driver.person.publicID === value;
        });

if (driverVM.person && otherDriver) {
    const policyHolder = driverList.find(
        (driver) => driver.isPolicyHolder
    );

    driverList.forEach((driver) => {
        if (
            driver.relationshipToInsured_ML === SPOUSE &&
            driver.spouseID_ML &&
            (isQB
                ? policyHolder?.person?.publicID
                : true) &&
            driver.spouseID_ML !== policyHolder.person.publicID
        ) {
            driver.relationshipToInsured_ML = null;
        }

                const ifSpouseNoLongerValid =
                    driver.spouseID_ML &&
                    driver.spouseID_ML === driverVM.spouseID_ML.value &&
                    driver !== driverVM.value;

                driver.spouseID_ML = ifSpouseNoLongerValid ? null : driver.spouseID_ML;
            });

            setOtherDriverInfo(otherDriver);

            driverVM.relationshipToInsured_ML.value =
                otherDriver.relationshipToInsured_ML === SELF
                    ? SPOUSE
                    : driverVM.relationshipToInsured_ML.value;
        }

        refreshData();
    };

    const validateUnlicensedRequiredError = (driver) => {
        const element = document.getElementById('isUnlicensedErrorSpan');
        if (element && !isAMP && driver.isHouseholdSpouse && driver.isUnlicensed === undefined) {
            element.style.visibility = "visible";
            return false;
        }
        if (element) {
            element.style.visibility = "hidden";
        }
        return true;
    };

    const onAddSpouseClick = () => {
        const spouseVM = addNewSpouseDriverModel();
        const addDriverModelTitle = `${translator(addSpouseMessages.AddSpousePopupTitle)}`;

        const onAddDriver = (spouseDriver) => {
            if (!validateUnlicensedRequiredError(spouseDriver)) {
                return false;
            }

            if (!spouseDriver.aspects.valid || !spouseDriver.aspects.subtreeValid) {
                return false;
            }

            populateDriverPerson(spouseDriver.value);

            if(!isCompRaterSpouseFeature) {
                spouseDriver.partialDriverAdditionFlag_ML = false;
                spouseDriver.relationshipToInsured_ML = 'spouse';
            }

            spouseDriver.type_ML = 'rated';

            // The user has identified the spouse as a household member, not a rated driver.
            if (spouseDriver.isHouseholdSpouse === true) {
                spouseDriver.type_ML.value = 'household'; // household
                if(isModernProduct) {
                    spouseDriver.type_ML.value = 'nondriver';
                }
                spouseDriver.status_ML = 'never_licensed';
                if (!isAMP && (spouseDriver.isUnlicensed && spouseDriver.isUnlicensed.code === 'false')) {
                    spouseDriver.status_ML = 'abroad';
                }
            }

            // updateModifiers();

            // Assign relationships between driver and newly-created spouse.
            spouseDriver.maritalStatus_ML = 'M';
            spouseDriver.spouseID_ML.value = driverVM.person.publicID.value;
            driverVM.spouseID_ML.value = spouseDriver.person.tempID.value;

            submissionVM.lobData.personalAuto.coverables.drivers.pushElement(spouseDriver);
            // updateWizardData(submissionVM);

            return true;
        };

        const showUnlicensedOrAbroadFollowup = (value, path) => {
            if (["isHouseholdSpouse"].includes(path)) {
                const element = document.getElementById('unlicensedRowContainer');
                if (!isAMP && element) {
                    if (value) {
                        element.style.visibility = "visible";
                    } else {
                        element.style.visibility = "hidden";
                    }
                }
            } else if (["isUnlicensed"].includes(path)) {
                const element = document.getElementById('isUnlicensedErrorSpan');
                if (element && value !== undefined) {
                    element.style.visibility = "hidden";
                }
            }
        };

        const nbGenderState = _.get(submissionVM, 'baseData.isValidNonBinaryGenderState_Ext.value');

        const overrideProps = {
            warningText: {
                visible: isCompRaterSpouseFeature,
            },
            relationship: {
                availableValues: 
                    relationshipTypelist.filter((relash) => {
                        if(driverVM.relationshipToInsured_ML.value.code !== 'self'&& relash.code === 'spouse') {
                            return false;
                        }
                        
                        return relash.code !== 'self';
                    }).map((relash) => ({
                        code: relash.code,
                        name: translator({
                            id: relash.name,
                            defaultMessage: relash.name
                        })
                    }))
            },
            nameDetailsComponent: {
                personVM: spouseVM.person,
                // to identify as a Compnent for FormModalPopover to attach onValidate function
                isComponent: true
            },
            dobHelp: {
                visible: !isPolicyChange && !isGPA
            },
            householdRow: {
                label: isAMP ? translator(addSpouseMessages.AMPNotALicensedDriver) : translator(addSpouseMessages.NotALicensedDriver)
            },
            householdHelp: {
                visible: !isPolicyChange && !isGPA
            },
            genderRow: {
                path: "gender",
                availableValues: getFilteredGender(nbGenderState),
                className: isQB ? "row col-md-4 vertical-spacing-20 radioButtonQnB" : "row col-md-4 vertical-spacing-20"
            },
            dobRow: {
                className: isQB ? 'row help-text-column vertical-spacing-40 col-md-12' : 'row help-text-row vertical-spacing-40 col-md-12'
            },
            dobTooltipContainer: {
                className: isQB ? 'col-md-6 tooltipContainerOverride tooltipContainerOverrideQB': 'col-md-6 tooltipContainerOverride'
            },
            householdRowTooltipContainer: {
                className: isQB ? 'col-md-6 tooltipContainerOverride tooltipContainerOverrideQB': 'col-md-6 tooltipContainerOverride'
            },
            householdRowContainer: {
                className: isQB ? 'help-text-column vertical-spacing-20 max-width-null' : 'help-text-row-household vertical-spacing-20 max-width-null'
            }
        };

        const componentProps = {
            title: addDriverModelTitle,
            uiProps: addSpouseMetadata.pageContent,
            viewModel: spouseVM,
            actionBtnLabel: addSpouseMessages.AddSpousePopupTitle,
            cancelBtnLabel: commonMessages.mlCancel,
            modalBodyStyles: addSpouseModalBodyStyles,
            overrideProps: overrideProps,
            customComponentResolvers: {
                mlnamedetailscomponent: MLNameDetailsComponent,
                MLTooltipComponent
            },
            onSaveAction: onAddDriver,
            onFieldValueChange: (value, path) => {
                showUnlicensedOrAbroadFollowup(value, path);
            }
        };

        modalApi.showModal(
            <FormModalPopover {...componentProps} />
        ).then((spouseDriver) => {
            handleSpouseModelChange(spouseDriver);
        }, _.noop);
    };

    const resolvers = {
        resolveCallbackMap: {
        }
    };
    
    const overrides = {
        labelText: {
            content: getSpouseLabel(driverVM.value)
        },
        spouseDropdown: {
            value: getSpouseIDValue(),
            availableValues: personList.map((person) => {
                return {
                    code: person.publicID || person.tempID,
                    name: getSpouseDisplayList(person)
                };
            }).filter((option) => option.code && option.name),
            onValueChange: (value) => {
                setCorrespondingSpouse(value);
            }
        },
        selectHelpText: {
            visible: showValidationErrors && checkNoSpouse(driverVM.value)
        }
    };

    return (
        <ViewModelForm
            uiProps={metadata.componentContent}
            model={driverVM}
            classNameMap={styles}
            overrideProps={overrides}
            callbackMap={resolvers.resolveCallbackMap}
        />
    );
}

SpousePopoverComponent.propTypes = {
    submissionVM: PropTypes.shape({
        lobData: PropTypes.shape({
            personalAuto: PropTypes.shape({
                coverables: PropTypes.shape({
                    drivers: PropTypes.shape({
                        pushElement: PropTypes.func
                    })
                })
            })
        }),
        baseData: PropTypes.shape({
            policyAddress: PropTypes.shape({
                value: PropTypes.shape({})
            })
        })
    }).isRequired,
    driverVM: PropTypes.shape({
        requiresProof_ML: PropTypes.shape({
            value: PropTypes.bool
        }),
        spouseID_ML: PropTypes.shape({
            value: PropTypes.string
        }),
        maritalStatus_ML: PropTypes.shape({
            value: PropTypes.shape({
                code: PropTypes.string
            })
        }),
        person: PropTypes.shape({
            publicID: PropTypes.shape({
                value: PropTypes.string
            }),
            value: PropTypes.shape({})
        }),
        relationshipToInsured_ML: PropTypes.shape({
            value: PropTypes.shape({
                code: PropTypes.string
            })
        }),
        value: PropTypes.shape({
            fixedId: PropTypes.number,
            tempID: PropTypes.string
        })
    }).isRequired,
    driverList: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    handleSpouseModelChange: PropTypes.func.isRequired,
    updateModifiers: PropTypes.func.isRequired,
    isPolicyChange: PropTypes.bool,
    showValidationErrors: PropTypes.bool,
    isPersonalUmbrella: PropTypes.bool,
    disableAddNewSpouse: PropTypes.bool
};

SpousePopoverComponent.defaultProps = {
    isPolicyChange: false,
    isCompRaterSpouseFeature: false,
    showValidationErrors: false,
    isPersonalUmbrella: false,
    disableAddNewSpouse: false
};

export default SpousePopoverComponent;
